import { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";

const MusicContext = createContext();

export const useMusic = () => useContext(MusicContext);

export const MusicProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const startedRef = useRef(false);

  const startMusic = useCallback(() => {
    if (startedRef.current || !audioRef.current) return;
    startedRef.current = true;
    audioRef.current.volume = 0.3;
    audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {
      startedRef.current = false;
    });
  }, []);

  useEffect(() => {
    const events = ["click", "touchstart", "scroll", "keydown", "mousemove"];
    events.forEach(evt => window.addEventListener(evt, startMusic, { once: true, passive: true }));
    return () => {
      events.forEach(evt => window.removeEventListener(evt, startMusic));
    };
  }, [startMusic]);

  const pause = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  const fadeInRef = useRef(null);

  const resumeWithFade = useCallback((delay = 0) => {
    if (!audioRef.current) return;
    setTimeout(() => {
      if (!audioRef.current) return;
      audioRef.current.volume = 0;
      audioRef.current.play().then(() => {
        setIsPlaying(true);
        let vol = 0;
        if (fadeInRef.current) clearInterval(fadeInRef.current);
        fadeInRef.current = setInterval(() => {
          vol += 0.02;
          if (vol >= 0.3) {
            vol = 0.3;
            clearInterval(fadeInRef.current);
          }
          if (audioRef.current) audioRef.current.volume = vol;
        }, 50);
      }).catch(() => {});
    }, delay);
  }, []);

  const resume = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, []);

  const toggle = useCallback(() => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  return (
    <MusicContext.Provider value={{ isPlaying, pause, resume, resumeWithFade, toggle }}>
      <audio ref={audioRef} src="/music.mp3" loop preload="auto" />
      {children}
    </MusicContext.Provider>
  );
};
